/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { Field, ErrorMessage, FieldArray } from 'formik';
import {
   Grid,
   TextField,
   Box,
   Typography,
   Select,
   MenuItem,
   InputLabel,
   FormControl,
   Chip,
   FormHelperText,
} from '@mui/material';
// import CustomDatePicker from '../../Components/MUI/CustomDatePicker';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '../../Utils/RichEditor';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useSelector } from 'react-redux';
import { Button } from '@mui/material';

function StepFive({ isSubmitting, values, setFieldValue, touched, errors, initialValues }) {
   // Redux States
   const bankList = useSelector((state) => state.contractState.bankDetails);

   const [BankOptions, setBankOptions] = useState([]);
   const [selectedBank, setSelectedBank] = useState([]);
   const [bankSelectValue, setBankSelectValue] = useState('');

   useEffect(() => {
      // Update bank options and selected banks whenever bank list or Formik bank ids change
      setBankOptions(bankList || []);

      const ids = values?.Bank_Details || [];
      const selected = ids.map((id) => bankList?.find((item) => item.id === id)).filter(Boolean);
      setSelectedBank(selected);
      // reset the select UI after syncing
      setBankSelectValue('');
   }, [bankList, values?.Bank_Details]);

   const handleAddBank = (BankId) => {
      if (!BankId) return;
      const id = BankId;
      const existingIds = values?.Bank_Details || [];
      // prevent duplicates
      if (existingIds.includes(id)) {
         setBankSelectValue('');
         return;
      }
      const bank = bankList?.find((item) => item.id === id);
      const newIds = [...existingIds, id];
      setFieldValue('Bank_Details', newIds);
      setSelectedBank((prev) => [...(prev || []), bank].filter(Boolean));
      setBankSelectValue('');
   };

   const handleRemoveBank = (bankId) => {
      const updatedBank = (selectedBank || []).filter((item) => item.id !== bankId);
      setSelectedBank(updatedBank);
      const updatedBankIds = updatedBank.map((item) => item.id);
      setFieldValue('Bank_Details', updatedBankIds);
      setBankSelectValue('');
   };

   // Redux State for Contract Prefilled Data
   const contractPrefilledData = useSelector(
      (state) => state.contractState?.prefilledData?.response,
   );

   // contractTypePreFillData: set when a contract TYPE was selected in StepOne.
   // When present, StepOne already called setFieldValue for all 6 StepSix fields
   // with correct data. This effect must NOT overwrite those values.
   const contractTypePreFillData = useSelector(
      (state) => state.contractState?.contractTypePreFillData,
   );

   // Set Pre Filled Data For Step Six (old opportunity-based pre-fill)
   // GUARD: skip entirely if contract-type pre-fill has already set these fields.
   React.useEffect(() => {
      if (contractTypePreFillData) {
         return;
      }

      // CRITICAL FIX: When switching contract types, clear all 6 pre-fill fields
      // This ensures that switching from "Standard SaaS" to "Simple Service Agreement"
      // actually clears the old pre-fill values, not keep them from previous type
      const fieldsToReset = [
         'general_terms_and_conditions',
         'project_termination_terms',
         'payment_terms',
         'application_ownership',
         'liability',
         'legal_jurisdiction',
      ];
      fieldsToReset.forEach((key) => {
         setFieldValue(key, '');
      });

      if (contractPrefilledData) {
         // Format All the Data
         const gen_terms = `
  <ul>
    ${contractPrefilledData[3]?.data?.general_condition
               ?.map((term, index, array) => {
                  // Start sub-points at index 4 and go until the second-to-last item
                  if (index === 4) {
                     return `
            <li>${term}
              <ul>
                ${array
                           .slice(5, array.length - 2)
                           ?.map((subTerm) => `<li>${subTerm}</li>`)
                           .join('')}
              </ul>
            </li>
          `;
                  }
                  // Render the last two items after the sub-points
                  else if (index >= array.length - 2) {
                     return `<li>${term}</li>`;
                  }
                  // Render initial items as regular list items
                  else if (index < 4) {
                     return `<li>${term}</li>`;
                  }
                  // Do nothing for items already added as sub-points
                  else {
                     return '';
                  }
               })
               .join('')}
  </ul>
`;

         const project_termination_terms = `
  <ul>
    ${contractPrefilledData[3]?.data?.project_termination_term
               ?.map((term) => `<li>${term}</li>`)
               .join('')}
  </ul>
`;
         const payment_terms = `
  <ul>
    ${contractPrefilledData[3]?.data?.payment_term_condition
               ?.map((term) => `<li>${term}</li>`)
               .join('')}
  </ul>
`;

         // Set All the Data
         setFieldValue('general_terms_and_conditions', gen_terms);
         setFieldValue('project_termination_terms', project_termination_terms);
         setFieldValue('payment_terms', payment_terms);
         setFieldValue('application_ownership', contractPrefilledData[4]?.data?.app_owner);
         setFieldValue('liability', contractPrefilledData[5]?.data?.liability);
         setFieldValue('legal_jurisdiction', contractPrefilledData[6]?.data?.legal_jury);
      }
   }, [contractPrefilledData, contractTypePreFillData, setFieldValue]);

   return (
      <>
         <div className="card stepCard">
            <Box className="card-body" sx={{ padding: '15px' }}>
               <Grid item xs={12}>
                  <Box className="commonBox">
                     <Typography variant="h6" gutterBottom align="left">
                        Select Bank Account <span style={{ color: 'red' }}>*</span>
                        <span
                           style={{
                              display: 'inline-block',
                              backgroundColor: '#d32f2f',
                              color: '#fff',
                              fontSize: '0.65em',
                              fontWeight: 500,
                              borderRadius: '16px',
                              padding: '2px 12px',
                              marginLeft: '8px',
                              verticalAlign: 'middle',
                           }}
                        >
                           Required
                        </span>
                     </Typography>
                     <Grid container spacing={2}>
                        <Grid item xs={12}>
                           <FormControl fullWidth variant="outlined"
                              error={Boolean(touched?.Bank_Details && errors?.Bank_Details)}
                           >
                              {selectedBank?.length === 0 && <InputLabel
                                 id="bank-account-label"
                                 sx={{ backgroundColor: '#fff', px: 0.5 }}
                              >
                                 Select Bank Account
                              </InputLabel>}
                              <Select
                                 labelId="bank-account-label"
                                 value={bankSelectValue}
                                 onChange={(e) => {
                                    handleAddBank(e.target.value);
                                 }}
                                 displayEmpty
                                 renderValue={() => {
                                    const selectedBank = (values.Bank_Details || [])
                                       .map((id) =>
                                          bankList?.find((b) => b.id === id)
                                       )
                                       .filter(Boolean);

                                    if (!selectedBank.length) {
                                       return 'Select Bank Account';
                                    }

                                    return (
                                       <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                          {selectedBank.map((bank) => (
                                             <Chip
                                                key={bank.id}
                                                label={bank.bank_name}
                                                size="small"
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onDelete={() => handleRemoveBank(bank.id)}
                                             />
                                          ))}
                                       </Box>
                                    );
                                 }}
                              >
                                 <MenuItem value="" disabled>
                                    Select Bank Account
                                 </MenuItem>
                                 {BankOptions?.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                       {option.bank_name} - ********
                                       {option.account_no.substring(
                                          option.account_no.length - 4,
                                       )} : {option.ifsc_Code}
                                    </MenuItem>
                                 ))}
                              </Select>
                              {touched.Bank_Details && errors.Bank_Details && (
                                 <FormHelperText sx={{ color: "red" }}>{errors.Bank_Details}</FormHelperText>
                              )}
                           </FormControl>

                           <ErrorMessage
                              name="selected_bank_account"
                              component="div"
                              style={{ color: 'red' }}
                           />
                        </Grid>
                        {/* <Grid item xs={12}>
                           <Typography variant="subtitle1">
                              Selected Bank Account Details:
                           </Typography>
                           {selectedBank.length > 0 && (
                              <Box>
                                 {selectedBank?.map((Bank, index) => (
                                    <Box
                                       key={index}
                                       sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          mb: 1,
                                       }}
                                    >
                                       <Typography variant="body2">
                                          {Bank.bank_name} - ********
                                          {Bank.account_no?.substring(
                                             Bank.account_no.length - 4,
                                          )} : {Bank.ifsc_Code}
                                       </Typography>
                                       <Button
                                          onClick={() => handleRemoveBank(Bank.id)}
                                          startIcon={<DeleteIcon />}
                                          size="small"
                                          sx={{ ml: 1 }}
                                       >
                                          Remove
                                       </Button>
                                    </Box>
                                 ))}
                              </Box>
                           )}
                        </Grid> */}
                     </Grid>
                  </Box>
               </Grid>
            </Box >
         </div >
      </>
   );
}

export default StepFive;
